'use strict';

define('vb/private/stateManagement/context/containerContext',[
  'vb/private/constants',
  'vb/private/utils',
  'vb/private/services/serviceResolverFactory',
  'vb/private/translations/translationContext',
  'vb/private/stateManagement/context/withEnvContext',
], (Constants, Utils, ServiceResolverFactory, TranslationContext, WithEnvContext) => {
  /**
   * @typedef {import('vb/private/stateManagement/container').default} Container
   * @typedef {import('vb/private/stateManagement/variable').default} Variable
   * @typedef {import('vb/private/stateManagement/context/baseContext').default} BaseContext
   */

  // A map to keep a reference to the baseContext after they are initialized
  const baseContextMap = new WeakMap();

  /**
   * a 'base' class for defining a set of properties available for binding, expression, and action contexts.
   * Intended to be subclassed for each of the various container types.
   *
   *
   *                                      ContainerContext
   *                                            |
   *                 +--------------------------+----------------------------+
   *                 |                          |                            |
   *             FlowContext               PageContext            ContainerExtensionContext
   *                 |                                                       |
   *      +----------+-----------+                     +---------------------+--------------------+------------------+
   *      |                      |                     |                     |                    |                  |
   * ApplicationContext    AppPackageContext    FlowExtensionContext  PageExtensionContext  LayoutExtensionContext   |
   *                                                                                                                 |
   *                                                                                           FragmentExtensionContext
   */
  class ContainerContext {
    /**
     * base class for subset of scope properties,
     * plus possibly some extra (like 'responsive', 'translations', etc) that doesn't belong in the scope.
     *
     * this is meant to create the bindable properties that are visible to the author.
     *
     * this class is intended to be subclassed.
     *
     * All namesakes like $flow, $application, $page, etc, will be subclasses of ContainerContexts.
     *
     * One 'weird' addition is getVariable(), used by AssignVariableAction.  When creating a top-level
     * namesake ContainerContext, this method is added to allow access to the Scope variables. (This could
     * go in ActionHelpers, but I wasn't sure if we should expose this to application authors creating custom Actions).
     *
     * @param {Container} container
     */
    constructor(container) {
      const propDescriptors = {
        application: {
          get() {
            let app;
            // Depending if the container is in a App UI or not, the content of the application
            // object is different. When in an App UI, the application object is only what is
            // defined in the interface.
            const appPackage = container.package;
            if (appPackage) {
              const extensionApplication = appPackage.application.extensions[appPackage.extensionId];
              app = extensionApplication.expressionContext.base;
            } else {
              app = container.application.expressionContext;
            }

            // and redefine the property as a value for immediate access on the next get
            Object.defineProperty(this, 'application', {
              value: app,
              enumerable: true,
              configurable: true,
            });

            return app;
          },
          enumerable: true,
          configurable: true,
        },
        /**
         * baseContext is available on the expressionContext of any container but it
         * is only exposed by extensions of this container.
         * baseContext is the $base scope on extensions of this container
         */
        baseContext: {
          /**
           * @returns {BaseContext}
           */
          get() {
            let baseContext = baseContextMap.get(this);
            if (!baseContext) {
              // Lazily create the BaseContext because it's only needed when
              // an extension is using an expression with $base
              /** @type {BaseContext} */
              baseContext = new (Object.getPrototypeOf(this).constructor.BaseContextType)(container);
              baseContextMap.set(this, baseContext);
            }

            return baseContext;
          },
          enumerable: true,
          configurable: true,
        },
        listeners: {
          get() {
            return container.eventListeners;
          },
          enumerable: true,
          configurable: true,
        },
        functions: {
          get() {
            return container.functions;
          },
          enumerable: true,
          configurable: true,
        },
        modules: {
          get() {
            return container.modules;
          },
          enumerable: true,
          configurable: true,
        },
        chains: {
          get() {
            return container.chains;
          },
          enumerable: true,
          configurable: true,
        },
        enums: {
          get() {
            return container.enums;
          },
          enumerable: true,
          configurable: true,
        },
        /**
         * <container context>.translations
         * We expose V2 Strings only in the $translations EL context (as well as V1 Strings).
         * This is a self updating property descriptor: the bundles context is loaded on the first get, and then
         * the property descriptor rewrites itself with that value so it doesn't have to be calculated again.
         */
        translations: TranslationContext.createV1TranslationsPropertyDescriptor(this, container),
        /**
         * Accessor for webSockets
         */
        webSockets: {
          get() {
            const webSockets = {};
            Object.entries(container.webSockets).forEach(([name, ws]) => {
              Object.defineProperty(webSockets, name, {
                get() {
                  return ws.instance;
                },
                enumerable: true,
                configurable: false,
              });
            });
            return webSockets;
          },
          enumerable: true,
          configurable: true,
        },
      };

      /**
       * Accessor for the variables, metadata and constants namespaces
       */
      [Constants.VariableNamespace.VARIABLES,
        Constants.VariableNamespace.METADATA,
        Constants.VariableNamespace.CONSTANTS,
      ].forEach((nameSpace) => {
        propDescriptors[nameSpace] = {
          get() {
            const { scope } = container;
            // protect again variables not be being initialized
            return scope && scope.variableNamespaces[nameSpace];
          },
          set(value) {
            // setting metadata for mocking purposes only during action chain testing
            if (Utils.isActionChainTestMode() && nameSpace === Constants.VariableNamespace.METADATA) {
              const { scope } = container;
              scope.variableNamespaces[nameSpace] = value;
            }
          },
          enumerable: true,
          configurable: true,
        };
      });

      Object.defineProperties(this, propDescriptors);

      // special case, add the getVariable method from Scope
      /**
       * Returns the variable instance
       * @param {...string} args an array consisting of name and optionally namespace
       * @return {Variable}
       */
      this.getVariable = (...args) => {
        const { scope } = container;
        const [name, namespace] = args;

        return scope && scope.getVariable(name, namespace);
      };
    }

    dispose() {
      const baseContext = baseContextMap.get(this);
      if (baseContext) {
        baseContext.dispose();
        baseContextMap.delete(this);
      }

      // Delete every property because components sometime holding onto those properties preventing
      // the parent object from getting garbage collected.
      // eslint-disable-next-line guard-for-in, no-restricted-syntax
      for (const prop in this) { delete this[prop]; }
    }

    /**
     * @param {Object} container
     *
     * this method is static because we need to be able to create the available contexts BEFORE
     * the individual XXXContext objects are creates, because we base the viewModel on the availableContexts object.
     *
     * these static methods still use class hierarchy for inheriting property accessors.
     *
     * @returns {Object}
     */
    static getAvailableContexts(container) {
      // using defineProperties, and accessors for multiple reasons:
      // - make these un-settable
      // - allow us to create the ViewModel from this object, before calling run() (for Page in particular)
      // - for Page, something needs $flow and $application before Page.run(), so we don't actually
      // have an expressionContext the first time getAvailableContexts() is called - we don't want to create the
      // Scope before then. So, rather than create a $flow/$application only version of the available contexts,
      // using accessors allows the 'value' for $page (and its properties) be created after this
      // $todo: see if we can re-work the calls to getAvailableContexts() before run()
      const availableContexts = {
        get $global() {
          return container.expressionContext.application;
        },

        get $application() {
          return container.expressionContext.application;
        },

        get $variables() {
          return container.expressionContext[Constants.VariableNamespace.VARIABLES];
        },

        get $enums() {
          return container.expressionContext.enums;
        },

        get $metadata() {
          return container.expressionContext[Constants.VariableNamespace.METADATA];
        },

        get $constants() {
          return container.expressionContext[Constants.VariableNamespace.CONSTANTS];
        },

        get $chains() {
          return container.expressionContext.chains;
        },

        get $functions() {
          return container.expressionContext.functions;
        },

        get $listeners() {
          return container.expressionContext.listeners;
        },

        get $modules() {
          return container.expressionContext.modules;
        },

        get $imports() {
          // imports are not exposed on the container context, instead only export '$imports' to limit scope to current
          // container. This will prevent '$flow.imports', '$application.imports' explicitly
          return container.imports;
        },

        get $env() {
          return container.application.runtimeEnvironment.getEnvironmentContext();
        },

        get $translations() {
          // Get the full translations context (V1 and V2 strings) for exposure in $translations.
          const bundleContext = TranslationContext.createContext(container.bundles);

          // Redefine the property as a value for immediate access on the next get.
          TranslationContext.defineTranslationsProperty(availableContexts, '$translations', bundleContext);

          return bundleContext;
        },

        /**
         * Object containing properties that provide help with resources resolutions in the context of the container.
         * Currently only property is serviceResolver, which is an opaque object that service layer
         * needs to be able to resolve endpoint references for the container.
         */
        get $vbContext() {
          const vbContext = {
            serviceResolver: ServiceResolverFactory.getResolverForContainer(container),
            registryHelper: {
              async getAppUiInfoDigest() {
                return container.application.extensionRegistry.getAppUiInfoDigest();
              },
            },
          };
          return vbContext;
        },

        get $webSockets() {
          return container.expressionContext.webSockets;
        },

        // Internal runtime expression helper used by generated binding expressions to evaluate rules with
        // explicit row data. It lives on available contexts for expression evaluation, but is not part of the
        // public action-chain TypeScript context contract.
        /**
         * @param {*} envOverrides
         * @this {Object}
         * @returns {Object}
         */
        $withEnv(envOverrides) {
          const context = this || availableContexts;
          return WithEnvContext.createWithEnvContext(context, envOverrides);
        },
      };

      Object.defineProperties(availableContexts, {
        $withEnv: {
          writable: false,
        },
        clone: {
          configurable: true,
          value: ContainerContext.cloneContext.bind(availableContexts),
        },
        addAccessor: {
          configurable: true,
          value: ContainerContext.addAccessor.bind(availableContexts),
        },
      });

      // All containers have the $extension scope even when the container is not
      // defined in an extension. This is to provide a consistent behavior across
      // all container in all type of application and not breaking app already using
      // $extension.path in non extension case like for $layout
      Object.defineProperty(availableContexts, '$extension', {
        enumerable: true,
        configurable: true,
        value: {
          get [Constants.PATH_VARIABLE]() {
            return container.absoluteUrl;
          },
        },
      });

      if (container.package) {
        Object.defineProperties(availableContexts, {
          // Redefine $application to point to the App UI context
          $application: {
            enumerable: true,
            configurable: true,
            get() {
              return container.package.expressionContext;
            },
          },
          $base: {
            enumerable: true,
            configurable: true,
            get() {
              return container.expressionContext.baseContext;
            },
          },
        });

        // Redefine $extension.base
        Object.defineProperty(availableContexts.$extension, Constants.ExtensionContextProperties.BASE, {
          enumerable: true,
          configurable: true,
          get() {
            return container.expressionContext.baseContext;
          },
        });

        //
        // Add V2 Translation Bundles imported from other Extensions
        //
        if (container.bundles && container.bundles.hasExtensionBundleV2Definitions()) {
          // $extension.<extId>.translations from its "imports"/"translations"/"<extId>"
          TranslationContext.addExtensionsContexts(container.bundles, availableContexts.$extension);

          // $base.translations from its "imports"/"translations"/"base"
          TranslationContext.addBaseExtensionContexts(container, availableContexts.$base);
        }
      }

      return availableContexts;
    }

    /**
     * this is a regular function, so that 'this' is bindable; shared with ActionChainContexts
     *
     * a simple shallow-ish clone, to handle accessors via property descriptors
     * also creates new arrays at ONLY the top level.
     *
     * good enough for contexts, for now, but requirements could change is contexts need more functionality
     */
    static cloneContext() {
      const cloneObj = {};

      const propDescriptors = {
        clone: {
          enumerable: false,
          value: ContainerContext.cloneContext.bind(cloneObj),
        },
        addAccessor: {
          enumerable: false,
          value: ContainerContext.addAccessor.bind(cloneObj),
        },
      };

      Object.keys(this).forEach((propertyName) => {
        const def = Object.getOwnPropertyDescriptor(this, propertyName);
        if (Array.isArray(def.value)) {
          def.value = def.value.slice();
        }
        propDescriptors[propertyName] = def;
      });

      return Object.defineProperties(cloneObj, propDescriptors);
    }

    /**
     * when the container needs to add something that does not already exist, instead of just defining a property
     * on the object, this is how it should be added.  Makes it discoverable, and debuggable.
     * For example, ForEach action adds a $current
     */
    static addAccessor(contextName, accessor) {
      if (contextName && typeof accessor === 'function') {
        Object.defineProperty(this, contextName, {
          enumerable: true,
          configurable: true,
          get: accessor,
        });
      }
    }
  }

  return ContainerContext;
});

